import React, { useContext } from 'react';
import { Tier, TierConfig } from '@gamiphy/bl/level';
import { Purchase } from '@gamiphy/bl/purchases';
import { App } from '@gamiphy/bl/app';
import { Theme } from '@gamiphy/bl/theme';
import {
  LevelsType, LoyaltyColors, LoyaltyImages, LoyaltyTexts, SectionsToggle,
} from '@gamiphy/bl/loyaltyPage';
import { Faq } from '@gamiphy/bl/faq';
import { User } from '@gamiphy/bl/user';
import { Discount } from '@gamiphy/bl/reward';
import { LoyaltyMission } from './types';
import { CampaignData } from './components/cashbackProgram/campaigns/types';

export type LoyaltyPageContext = {
  app?: App,
  user?: User,
  theme?: Theme,
  onSignUp: () => void,
  onSignIn: () => void,
  isPreview: boolean;
  isMobilePreview?: boolean;
  shouldRenderBenefits?: boolean;
  enabledSections: SectionsToggle;
  levelsType: LevelsType;
  colors: LoyaltyColors;
  texts: LoyaltyTexts;
  images: LoyaltyImages;
  faqs?: Faq[];
  tiers?: Tier[];
  currentTier?: Tier,
  nextTier?: Tier,
  progress?: number,
  discount?: Discount,
  maxGemsToRedeem: number,
  minGemsToRedeem: number,
  discountInactive: boolean,
  purchase?: Purchase;
  missions: LoyaltyMission[]
  benefits: [string, ...boolean[]][];
  mobileBenefitsData: { [key: string]: string[] };
  showWelcomeGiftBenefit: boolean,
  welcomeGift: string;
  isLoggedIn: boolean;
  isRealUser: boolean;
  isDraftUser: boolean;
  RedeemComponent: React.ReactNode;
  currentUserCashBack: number;
  tierConfig?: TierConfig,
  tiersAccumulatedValue: number,
  isCashbackEnabled: boolean,
  cashbackProgram: {
    isCashbackProgram: boolean,
    cashbackPercentage: number,
    tiersCashbackPercentages: {
      tier: number,
      cashbackPercentage: number,
    }[],
    minPurchaseAmount: number,
    bestFitDiscount: {
      value?: string,
      minRedemptionAmount: number,
      isWelcomeGiftDiscount: boolean,
    }
    onAssignPhone: (phone: string) => Promise<void>
    verifyPhone: (phone: string, otp: string) => Promise<void>,
    fetchDiscounts: () => Promise<void>,
    resendOtp: (phone: string) => Promise<void>,
    liveCampaigns: CampaignData[]
  },
  openLS?: () => void,
  redeemDiscount?: (value: number) => Promise<void>,
  toggleCouponModal?: (value: boolean) => void,
  handleGetPoints?: (e: React.MouseEvent) => void,
}

export const loyaltyPageContext = React.createContext<LoyaltyPageContext>({
  isPreview: false,
  shouldRenderBenefits: false,
  enabledSections: {
    heroSectionIsEnabled: false,
    promoteWelcomeGiftIsEnabled: false,
    promoteCashbackIsEnabled: false,
    howItWorksIsEnabled: false,
    activeMissionsIsEnabled: false,
    faqIsEnabled: false,
    levelsIsEnabled: false,
  },
  levelsType: LevelsType.cashbackAndBenefits,
  colors: {
    mainFontColor: '#ffffff',
    heroSectionTextColor: '#ffffff',
    promoteWelcomeGiftTextColor: '#ffffff',
    backgroundColor: '#ffffff',
    primaryColor: '#ffffff',
    buttonTextColor: '#ffffff',
  },
  texts: {
    heroSectionButtonText: { id: '', en: '' },
    heroSectionText: { id: '', en: '' },
    WelcomeGiftText: { id: '', en: '' },
    cashbackText: { id: '', en: '' },
  },
  images: { },
  tiers: [],
  benefits: [],
  missions: [],
  mobileBenefitsData: {},
  showWelcomeGiftBenefit: false,
  welcomeGift: '',
  isLoggedIn: false,
  isDraftUser: false,
  isRealUser: false,
  RedeemComponent: false,
  currentUserCashBack: 0,
  discountInactive: false,
  maxGemsToRedeem: Infinity,
  minGemsToRedeem: 0,
  tiersAccumulatedValue: 0,
  isCashbackEnabled: false,
  cashbackProgram: {
    isCashbackProgram: false,
    cashbackPercentage: 0,
    minPurchaseAmount: 0,
    tiersCashbackPercentages: [],
    bestFitDiscount: {
      value: '',
      minRedemptionAmount: 0,
      isWelcomeGiftDiscount: true,
    },
    liveCampaigns: [],
    onAssignPhone: async () => {},
    verifyPhone: async () => {},
    fetchDiscounts: async () => {},
    resendOtp: async () => {},
  },
  onSignUp: () => {},
  onSignIn: () => {},
});

export const LoyaltyPageProvider = loyaltyPageContext.Provider;
export const useLoyaltyPageContext = (): LoyaltyPageContext => useContext(loyaltyPageContext);
